import { Link, useLocation, useNavigate } from "react-router-dom";
import { 
  LayoutDashboard, Users, Home, LogOut, User, Settings, History, 
  Building2, Menu, X, Building, UserCheck, Calendar, Users as UsersIcon, 
  AlertTriangle, Link2, FileText, ChevronDown, ChevronRight, 
  Filter, Megaphone, ShieldCheck, Shield, BarChart3, Globe, Eye, ExternalLink,
  Download, Sparkles, Bell, RefreshCw, Mail, Inbox, Send, GraduationCap, Bot, Archive,
  Activity, Target, Zap, FolderOpen, HelpCircle, Ticket, MapPin, Share2, MessageCircle,
  TrendingUp, Palette, Briefcase, Landmark, CreditCard, Banknote, Handshake, CalendarCheck, Table2, DollarSign, Calculator,
  Monitor, Tv, QrCode, Presentation, Wrench
} from "lucide-react";
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Prefixo de rota: sempre /crm (App.js define todas as rotas com /crm/)
const getRoutePrefix = () => '';


const Sidebar = ({ onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [expandedMenus, setExpandedMenus] = useState({});
  const [showProfileSwitch, setShowProfileSwitch] = useState(false);
  const [perfisDisponiveis, setPerfisDisponiveis] = useState([]);
  const [switchingProfile, setSwitchingProfile] = useState(false);
  const [bolsaoCount, setBolsaoCount] = useState(0);
  const [showImpersonate, setShowImpersonate] = useState(false);
  const [allUsers, setAllUsers] = useState([]);
  const [impersonating, setImpersonating] = useState(false);
  const [originalUser, setOriginalUser] = useState(null);
  const prefix = getRoutePrefix();

  useEffect(() => {
    const user = JSON.parse(localStorage.getItem('user'));
    setCurrentUser(user);
    
    // Verificar se está impersonando
    const originalUserData = localStorage.getItem('originalUser');
    if (originalUserData) {
      const original = JSON.parse(originalUserData);
      setOriginalUser(original);
      setImpersonating(true);
      
      // Se o usuário original é DEV, carregar lista de usuários
      if (original.role === 'dev') {
        fetchAllUsers();
      }
    }
    
    // Carregar perfis disponíveis para todos os usuários
    if (user?.id) {
      fetchPerfisDisponiveis(user.id);
      fetchBolsaoCount();
      
      // Se for DEV e NÃO está impersonando, carregar lista de usuários
      if (user.role === 'dev' && !originalUserData) {
        fetchAllUsers();
      }
    }
  }, []);

  // Rota padrão baseada na role do usuário
  const getHomePath = () => {
    if (!currentUser) return `${prefix}/login`;
    if (currentUser.role === "comercial") return `${prefix}/funil`;
    return `${prefix}/dashboard`;
  };

  const fetchBolsaoCount = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      
      const response = await axios.get(`${BACKEND_URL}/api/bolsao/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBolsaoCount(response.data?.count || 0);
    } catch (error) {
      // Silenciar erro - não quebrar a sidebar
      setBolsaoCount(0);
    }
  };

  const fetchPerfisDisponiveis = async (userId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users/${userId}/perfis-disponiveis`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPerfisDisponiveis(response.data.perfis_disponiveis || []);
    } catch (error) {
      console.error("Erro ao carregar perfis:", error);
    }
  };

  const fetchAllUsers = async () => {
    const currentUser = JSON.parse(localStorage.getItem('user'));
    // Só carregar usuários se for dev ou admin
    if (!currentUser || !['dev', 'admin', 'proprietario'].includes(currentUser.role)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAllUsers(response.data || []);
    } catch (error) {
      // Silenciosamente ignora erro - funcionalidade só para admins
      setAllUsers([]);
    }
  };

  const startImpersonation = async (targetUser) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/impersonate`,
        { target_user_id: targetUser.id },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Salvar usuário original antes de impersonar
      const currentUserData = localStorage.getItem('user');
      const currentToken = localStorage.getItem('token');
      localStorage.setItem('originalUser', currentUserData);
      localStorage.setItem('originalToken', currentToken);
      
      // Aplicar novo token e usuário
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      
      toast.success(`Visualizando como ${targetUser.name}`);
      setShowImpersonate(false);
      
      // Recarregar página
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao impersonar usuário");
    }
  };

  const stopImpersonation = () => {
    // Restaurar usuário original
    const originalUserData = localStorage.getItem('originalUser');
    const originalToken = localStorage.getItem('originalToken');
    
    if (originalUserData && originalToken) {
      localStorage.setItem('user', originalUserData);
      localStorage.setItem('token', originalToken);
      localStorage.removeItem('originalUser');
      localStorage.removeItem('originalToken');
      
      toast.success("Voltando à sua conta");
      setTimeout(() => window.location.reload(), 500);
    }
  };

  const trocarPerfil = async (novoPerfil) => {
    if (!currentUser || switchingProfile) return;
    
    setSwitchingProfile(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BACKEND_URL}/api/users/${currentUser.id}/trocar-perfil`,
        { perfil: novoPerfil },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Atualizar token e usuário no localStorage
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('user', JSON.stringify(response.data.user));
      setCurrentUser(response.data.user);
      
      toast.success(`Perfil alterado para ${getPerfilLabel(novoPerfil)}`);
      setShowProfileSwitch(false);
      
      // Recarregar página para atualizar permissões
      setTimeout(() => window.location.reload(), 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao trocar perfil");
    } finally {
      setSwitchingProfile(false);
    }
  };

  const getPerfilLabel = (perfil) => {
    const labels = {
      dev: "DEV",
      proprietario: "Gestor",
      admin: "Administrador",
      diretor: "Diretor",
      diretor_comercial: "Diretor Comercial",

      comercial: "Comercial",
      financeiro: "Financeiro",
      atendimento: "Atendimento"
    };
    return labels[perfil] || perfil;
  };

  const getPerfilColor = (perfil) => {
    const colors = {
      dev: "bg-gradient-to-r from-red-500 to-pink-500 text-white",
      proprietario: "bg-gradient-to-r from-yellow-400 to-orange-500 text-white",
      admin: "bg-purple-100 text-purple-700",
      diretor: "bg-indigo-100 text-indigo-700",
      diretor_comercial: "bg-fuchsia-100 text-fuchsia-700",

      comercial: "bg-blue-100 text-blue-700",
      financeiro: "bg-emerald-100 text-emerald-700",
      atendimento: "bg-cyan-100 text-cyan-700",
    };
    return colors[perfil] || colors.comercial;
  };

  // Abrir automaticamente o submenu da página atual
  useEffect(() => {
    menuGroups.forEach(group => {
      if (group.items) {
        const hasActiveItem = group.items.some(item => location.pathname === item.path);
        if (hasActiveItem) {
          setExpandedMenus(prev => ({ ...prev, [group.label]: true }));
        }
      }
    });
  }, [location.pathname]);

  // Função para gerar os menus com o prefixo correto
  const getMenuGroups = () => [
    { path: `${prefix}/dashboard`, icon: BarChart3, label: "Dashboard", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "diretor", "diretor_comercial"], isTopLevel: true },
    {
      label: "Comercial",
      icon: Briefcase,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"],
      items: [
        { path: `${prefix}/funil`, icon: Filter, label: "Funil de Vendas", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
        { path: `${prefix}/clients`, icon: Users, label: "Leads", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
        { path: `${prefix}/unidades`, icon: Table2, label: "Unidades", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
        { path: `${prefix}/oportunidades`, icon: Target, label: "Oportunidades", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
        { path: `${prefix}/calculadora-financiamento`, icon: Calculator, label: "Calculadora de Investimento", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
        { path: `${prefix}/propostas`, icon: FileText, label: "Propostas", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
      ]
    },
    {
      label: "Painel de Negociações",
      icon: Monitor,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"],
      items: [
        { path: `${prefix}/negociacoes/ao-vivo`, icon: Activity, label: "Ao Vivo", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/negociacoes/app-tv`, icon: Tv, label: "App para TV", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
      ]
    },
    {
      label: "Índices Econômicos",
      icon: TrendingUp,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"],
      items: [
        { path: `${prefix}/indices/cub`, icon: TrendingUp, label: "CUB", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/indices/igpm`, icon: TrendingUp, label: "IGP-M", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/indices/incc`, icon: TrendingUp, label: "INCC", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/indices/ipca`, icon: TrendingUp, label: "IPCA", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
      ]
    },
    {
      label: "Negócios",
      icon: Handshake,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "financeiro", "atendimento"],
      items: [
        { path: `${prefix}/compradores`, icon: UserCheck, label: "Compradores", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "financeiro", "atendimento"] },
        { path: `${prefix}/contratos`, icon: FileText, label: "Contratos", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "financeiro", "atendimento"] },
        { path: `${prefix}/pagamentos`, icon: CreditCard, label: "Boletos", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "financeiro", "atendimento"] },
        { path: `${prefix}/repasse-bancario`, icon: Landmark, label: "Repasse Bancário", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "financeiro", "atendimento"] },
        { path: `${prefix}/reservas`, icon: CalendarCheck, label: "Reservas", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "financeiro", "atendimento"] },
        { path: `${prefix}/financeiro-sienge`, icon: DollarSign, label: "Financeiro Sienge", roles: ["dev", "proprietario"], requiresFlag: "acesso_sienge_financeiro" },
      ]
    },
    {
      label: "Rede de Corretores",
      icon: Users,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"],
      items: [
        { path: `${prefix}/rede-corretores/funil`, icon: TrendingUp, label: "Funil de Corretores", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"] },
        { path: `${prefix}/rede-corretores/corretores`, icon: Users, label: "Corretores", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"] },
        { path: `${prefix}/rede-corretores/imobiliarias`, icon: Building, label: "Imobiliárias", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"] },
        { path: `${prefix}/rede-corretores/tags`, icon: Filter, label: "Tags", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"] },
        { path: `${prefix}/rede-corretores/campanhas`, icon: Megaphone, label: "Campanhas", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"] },
        { path: `${prefix}/rede-corretores/disparos`, icon: Send, label: "Disparos", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"] },
      ]
    },
    {
      label: "Empreendimentos",
      icon: Building2,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento", "engenheiro"],
      items: [
        { path: `${prefix}/empreendimentos`, icon: Building2, label: "Empreendimentos", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento", "engenheiro"] },
        { path: `${prefix}/construtoras`, icon: Landmark, label: "Construtoras", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/bairros`, icon: MapPin, label: "Bairros", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/cidades`, icon: Globe, label: "Cidades", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
      ]
    },
    {
      label: "Documentação",
      icon: FolderOpen,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"],
      items: [
        { path: `${prefix}/propostas`, icon: FileText, label: "Propostas", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
      ]
    },
    {
      label: "Bolsão",
      icon: Archive,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial"],
      items: [
        { path: `${prefix}/bolsao`, icon: Archive, label: "Todos os Leads", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"], dynamicBadge: "bolsao" },
        { path: `${prefix}/bolsao/campanhas`, icon: Target, label: "Campanhas", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/bolsao/minha-campanha`, icon: Zap, label: "Minha Campanha", roles: ["comercial"] },
      ]
    },
    {
      label: "Operacional",
      icon: Calendar,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"],
      items: [
        { path: `${prefix}/agendamentos`, icon: Calendar, label: "Agendamentos", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
        { path: `${prefix}/lembretes`, icon: Bell, label: "Lembretes", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
      ]
    },
    {
      label: "Site",
      icon: Globe,
      roles: ["dev", "proprietario"],
      items: [
        { path: `${prefix}/dominios`, icon: Globe, label: "Domínios", roles: ["dev", "proprietario"] },
        { path: `${prefix}/admin/blog`, icon: FileText, label: "Blog", roles: ["dev", "proprietario"] },
        { path: `${prefix}/site-config`, icon: Globe, label: "Páginas", roles: ["dev", "proprietario"] },
      ]
    },
    {
      label: "Marketing",
      icon: Megaphone,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"],
      items: [
        { path: `${prefix}/landing-pages`, icon: FileText, label: "Landing Pages", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/mini-site`, icon: Link2, label: "Mini Site", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/marketing/qrcodes`, icon: QrCode, label: "QR Codes", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"], badge: "NOVO" },
      ]
    },
    {
      label: "E-Mail",
      icon: Mail,
      roles: ["dev", "proprietario"],
      badge: "Em breve",
      disabled: true,
      items: [
        { path: `${prefix}/email-marketing`, icon: Inbox, label: "Caixa de Entrada", roles: ["dev", "proprietario"], disabled: true },
        { path: `${prefix}/email-marketing?tab=sent`, icon: Send, label: "Enviados", roles: ["dev", "proprietario"], disabled: true },
        { path: `${prefix}/email-marketing?tab=campaigns`, icon: Megaphone, label: "Campanhas", roles: ["dev", "proprietario"], disabled: true },
        { path: `${prefix}/email-marketing?tab=contacts`, icon: Users, label: "Contatos", roles: ["dev", "proprietario"], disabled: true },
        { path: `${prefix}/email-marketing?tab=settings`, icon: Settings, label: "Configurações", roles: ["dev", "proprietario"], disabled: true },
      ]
    },
    {
      label: "InstaIMOB ADS",
      icon: TrendingUp,
      roles: ["dev", "proprietario"],
      items: [
        { path: `${prefix}/instaimob-ads`, icon: BarChart3, label: "Dashboard", roles: ["dev", "proprietario"], badge: "NOVO" },
        { path: `${prefix}/instaimob-ads/campanhas`, icon: MessageCircle, label: "Campanhas", roles: ["dev", "proprietario"] },
      ]
    },
    {
      label: "Academy",
      icon: GraduationCap,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"],
      items: [
        { path: `${prefix}/academy`, icon: GraduationCap, label: "Meus Cursos", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"] },
        { path: `${prefix}/academy/admin`, icon: Settings, label: "Gerenciar", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
      ]
    },
    {
      label: "Pixel Pro",
      icon: Activity,
      roles: ["dev", "proprietario", "gestor", "gestor_dados"],
      items: [
        { path: `${prefix}/pixel-pro`, icon: BarChart3, label: "Dashboard", roles: ["dev"] },
        { path: `${prefix}/visitantes`, icon: Eye, label: "Visitantes", roles: ["dev", "proprietario", "gestor", "gestor_dados"] },
        { path: `${prefix}/pixel-pro/events`, icon: Activity, label: "Eventos", roles: ["dev"] },
        { path: `${prefix}/pixel-pro/meta-capi`, icon: Share2, label: "Meta Ads (CAPI)", roles: ["dev", "proprietario", "gestor"] },
        { path: `${prefix}/pixel-pro/whatsapp`, icon: MessageCircle, label: "WhatsApp Analytics", roles: ["dev", "proprietario", "gestor"] },
        { path: `${prefix}/configuracoes`, icon: Settings, label: "Configurações", roles: ["dev", "proprietario"] },
        { path: `${prefix}/pixel-pro/rules`, icon: Settings, label: "Regras de Score", roles: ["dev"] },
      ]
    },
    {
      label: "Ferramentas",
      icon: Wrench,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"],
      items: [
        { path: `${prefix}/instaimob-slides`, icon: Presentation, label: "InstaIMOB Slides", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial", "comercial", "atendimento"], badge: "NOVO" },
      ]
    },
    {
      label: "Administração",
      icon: ShieldCheck,
      roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"],
      items: [
        { path: `${prefix}/equipe`, icon: UsersIcon, label: "Equipe", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/cargos-permissoes`, icon: Shield, label: "Cargos & Permissões", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/configuracoes`, icon: Settings, label: "Configurações", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
        { path: `${prefix}/action-logs`, icon: History, label: "Histórico", roles: ["dev", "proprietario", "admin", "diretor", "diretor_comercial"] },
      ]
    },
  ];

  const menuGroups = getMenuGroups();

  const toggleMenu = (label) => {
    setExpandedMenus(prev => ({ ...prev, [label]: !prev[label] }));
  };

  const getRoleBadge = (role) => {
    const badges = {
      dev: { label: "DEV", color: "bg-gradient-to-r from-red-500 to-pink-500 text-white" },
      proprietario: { label: "Gestor", color: "bg-gradient-to-r from-yellow-400 to-orange-500 text-white" },
      admin: { label: "Administrador", color: "bg-purple-500 text-white" },
      diretor: { label: "Diretor", color: "bg-gradient-to-r from-indigo-500 to-purple-600 text-white" },
      diretor_comercial: { label: "Diretor Comercial", color: "bg-gradient-to-r from-indigo-500 to-blue-600 text-white" },
      gestor_dados: { label: "Gestor de Dados", color: "bg-cyan-500 text-white" },

      comercial: { label: "Comercial", color: "bg-blue-500 text-white" },
      financeiro: { label: "Financeiro", color: "bg-emerald-500 text-white" },
      atendimento: { label: "Atendimento", color: "bg-cyan-500 text-white" }
    };
    return badges[role] || badges.comercial;
  };

  // Badge: Para DEV sempre mostra DEV, para outros usa perfil_ativo ou role
  const perfilAtivo = currentUser?.role === 'dev' ? 'dev' : (currentUser?.perfil_ativo || currentUser?.role);
  const badge = currentUser ? getRoleBadge(perfilAtivo) : null;

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname]);

  // Usar perfil_ativo para determinar visibilidade dos menus
  const isGroupVisible = (group) => {
    if (!currentUser) return false;
    const perfilParaPermissao = currentUser.perfil_ativo || currentUser.role;
    return group.roles.includes(perfilParaPermissao);
  };

  const isItemVisible = (item) => {
    if (!currentUser) return false;
    const perfilParaPermissao = currentUser.perfil_ativo || currentUser.role;
    // Itens com requiresFlag (ex: Financeiro Sienge) usam acesso individual
    // ativado manualmente (acesso_sienge_financeiro), não role padrão —
    // dev/proprietario sempre veem, os demais só se o flag estiver ligado.
    if (item.requiresFlag) {
      if (["dev", "proprietario"].includes(currentUser.role)) return true;
      return !!currentUser[item.requiresFlag];
    }
    return item.roles.includes(perfilParaPermissao);
  };

  return (
    <>
      {/* Mobile Header Bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 glass-panel border-b border-white/20 px-4 py-3 flex items-center justify-between">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="p-2 rounded-lg hover:bg-white/50 text-slate-700"
        >
          {isOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
        <Link to={getHomePath()} className="hover:opacity-80 transition-opacity">
          <img 
            src="/logo-instaimob-construtoras-black.png"
            alt="InstaIMOB" 
            className="h-8 w-auto object-contain"
          />
        </Link>
        <div className="flex items-center gap-2">
        </div>
      </div>

      {/* Overlay for mobile */}
      {isOpen && (
        <div 
          className="lg:hidden fixed inset-0 bg-black/50 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div className={`
        fixed lg:relative inset-y-0 left-0 z-50
        w-[294px] h-full glass-panel border-r border-white/20 p-4 lg:p-5 flex flex-col
        transform transition-transform duration-300 ease-in-out
        ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        lg:transform-none
      `}>
        <div className="mb-4">
          <div className="flex items-center justify-center mb-3">
            <Link to={getHomePath()} className="hover:opacity-80 transition-opacity">
              <img 
                src="/logo-instaimob-construtoras-black.png"
                alt="InstaIMOB CRM" 
                className="h-9 w-auto object-contain"
              />
            </Link>
          </div>
          
          
          {currentUser && (
            <div className="mt-3 border border-slate-200 bg-white rounded-xl p-4 relative group">
              {/* Botão de configurações no canto superior direito */}
              <div className="absolute top-2 right-2">
                <button
                  onClick={() => navigate(`${prefix}/profile`)}
                  data-testid="user-settings-button"
                  className="w-7 h-7 rounded-lg bg-slate-100 hover:bg-primary hover:text-white text-slate-500 flex items-center justify-center transition-all hover:scale-105 active:scale-95"
                  title="Configurações da conta"
                >
                  <Settings className="w-3.5 h-3.5" strokeWidth={2} />
                </button>
              </div>
              
              {/* Conteúdo do perfil */}
              <div className="flex flex-col items-center">
                {/* Foto com botão de troca de perfil ao redor */}
                <div className="relative">
                  {/* Anel de troca de perfil - só aparece se tem múltiplos perfis */}
                  {perfisDisponiveis.length > 1 && (
                    <button
                      onClick={(e) => { 
                        e.stopPropagation();
                        if (currentUser.role === 'proprietario') {
                          const perfilAtual = currentUser.perfil_ativo || currentUser.role;
                          const novoPerfil = perfilAtual === 'proprietario' ? 'comercial' : 'proprietario';
                          trocarPerfil(novoPerfil);
                        } else {
                          setShowProfileSwitch(!showProfileSwitch);
                        }
                      }}
                      disabled={switchingProfile}
                      data-testid="switch-profile-button"
                      className={`absolute inset-0 w-[104px] h-[104px] -m-1 rounded-full border-[3px] border-dashed transition-all duration-300 flex items-center justify-center ${
                        switchingProfile 
                          ? 'border-amber-500 animate-spin' 
                          : 'border-transparent hover:border-amber-500 group-hover:border-amber-300'
                      }`}
                      title={currentUser.role === 'proprietario' 
                        ? `Trocar para ${(currentUser.perfil_ativo || currentUser.role) === 'proprietario' ? 'Corretor' : 'Proprietário'}`
                        : 'Trocar perfil'
                      }
                    >
                      {/* Ícone de refresh que aparece no hover */}
                      <div className={`absolute -top-1 -right-1 w-6 h-6 rounded-full bg-amber-500 text-white flex items-center justify-center shadow-lg transition-all duration-200 ${
                        switchingProfile ? 'opacity-100 scale-100' : 'opacity-0 scale-75 group-hover:opacity-100 group-hover:scale-100'
                      }`}>
                        {switchingProfile ? (
                          <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <RefreshCw className="w-3 h-3" strokeWidth={2.5} />
                        )}
                      </div>
                    </button>
                  )}
                  
                  {/* Foto de perfil */}
                  {currentUser.photo_url ? (
                    <img 
                      src={currentUser.photo_url} 
                      alt={currentUser.name}
                      className="w-24 h-24 rounded-full object-cover border-3 border-primary/30 shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => navigate(`${prefix}/profile`)}
                    />
                  ) : (
                    <div 
                      className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/80 to-primary flex items-center justify-center text-white font-bold text-3xl shadow-lg cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => navigate(`${prefix}/profile`)}
                    >
                      {currentUser.name.charAt(0).toUpperCase()}
                    </div>
                  )}
                </div>
                
                {/* Nome médio */}
                <p className="mt-3 text-sm font-semibold text-slate-800 text-center truncate max-w-full">
                  {currentUser.name}
                </p>
                
                {/* Cargo/Perfil abaixo */}
                <span className={`mt-1 text-xs px-2.5 py-1 rounded-full font-medium ${badge?.color}`}>
                  {getPerfilLabel(currentUser.perfil_ativo || currentUser.role)}
                </span>
                
                {currentUser.role === 'proprietario' && currentUser.perfil_ativo && currentUser.perfil_ativo !== currentUser.role && (
                  <span className="text-[10px] text-slate-400 mt-1">(Proprietário)</span>
                )}
              </div>

              {/* Dropdown de troca de perfil - apenas para usuários que não são proprietário */}
              {showProfileSwitch && currentUser.role !== 'proprietario' && perfisDisponiveis.length > 1 && (
                <div className="absolute top-full left-0 right-0 mt-1 z-50 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
                  <div className="p-2 border-b border-slate-200">
                    <p className="text-xs font-medium text-slate-500">Trocar perfil</p>
                  </div>
                  <div className="p-1">
                    {perfisDisponiveis.map((perfil) => (
                      <button
                        key={perfil}
                        onClick={() => trocarPerfil(perfil)}
                        disabled={switchingProfile}
                        className={`w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md transition-colors ${
                          (currentUser.perfil_ativo || currentUser.role) === perfil
                            ? 'bg-primary/10 text-primary font-medium'
                            : 'hover:bg-slate-100 text-slate-700'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${getPerfilColor(perfil)}`} />
                        {getPerfilLabel(perfil)}
                        {(currentUser.perfil_ativo || currentUser.role) === perfil && (
                          <span className="ml-auto text-[10px] text-primary">Ativo</span>
                        )}
                      </button>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Botão Ver Como - Apenas para DEV */}
              {currentUser.role === 'dev' && !impersonating && (
                <div className="mt-3">
                  <button
                    onClick={() => setShowImpersonate(!showImpersonate)}
                    data-testid="impersonate-button"
                    className="w-full flex items-center justify-center gap-2 py-2 px-3 text-xs font-medium text-white bg-gradient-to-r from-red-500 to-pink-500 hover:from-red-600 hover:to-pink-600 rounded-lg transition-all shadow-md"
                  >
                    <Eye className="w-4 h-4" />
                    Ver como...
                  </button>
                  
                  {showImpersonate && (
                    <div className="absolute left-4 right-4 mt-2 z-50 bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden max-h-64 overflow-y-auto">
                      <div className="p-2 border-b border-slate-200 sticky top-0 bg-white">
                        <p className="text-xs font-medium text-slate-500">Selecione um usuário</p>
                      </div>
                      <div className="p-1">
                        {allUsers.filter(u => u.id !== currentUser.id).map((user) => (
                          <button
                            key={user.id}
                            onClick={() => startImpersonation(user)}
                            className="w-full flex items-center gap-2 px-3 py-2 text-xs rounded-md transition-colors hover:bg-slate-100 text-slate-700"
                          >
                            {user.photo_url ? (
                              <img src={user.photo_url} alt={user.name} className="w-6 h-6 rounded-full object-cover" />
                            ) : (
                              <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center text-primary text-[10px] font-bold">
                                {user.name?.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <div className="flex-1 text-left">
                              <p className="font-medium truncate">{user.name}</p>
                              <p className="text-[10px] text-slate-400">{getPerfilLabel(user.role)}</p>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Barra de impersonação ativa */}
              {impersonating && originalUser && (
                <div className="mt-3 p-2 bg-gradient-to-r from-amber-50 to-orange-50 border border-amber-200 rounded-lg">
                  <p className="text-[10px] text-amber-700 text-center mb-2">
                    Visualizando como outro usuário
                  </p>
                  <button
                    onClick={stopImpersonation}
                    className="w-full flex items-center justify-center gap-2 py-1.5 px-3 text-xs font-medium text-amber-700 bg-amber-100 hover:bg-amber-200 rounded-md transition-colors"
                  >
                    <X className="w-3 h-3" />
                    Voltar para {originalUser.name}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>

        <nav className="flex-1 overflow-y-auto space-y-1">
          {menuGroups.filter(isGroupVisible).map((group) => {
            // Item top-level (sem submenu)
            if (group.isTopLevel) {
              const isActive = location.pathname === group.path;
              return (
                <div key={group.label} className="space-y-0.5">
                  <Link
                    to={group.path}
                    className={`w-full flex items-center px-3 py-2 rounded-lg transition-colors text-xs ${
                      isActive
                        ? "bg-primary/10 text-primary"
                        : "text-slate-600 hover:bg-white/50 hover:text-slate-900"
                    }`}
                  >
                    <span className="font-medium">{group.label}</span>
                  </Link>
                </div>
              );
            }

            const isExpanded = expandedMenus[group.label];
            const visibleItems = group.items.filter(isItemVisible);
            const hasActiveItem = visibleItems.some(item => location.pathname === item.path || location.pathname + location.search === item.path);
            
            if (visibleItems.length === 0) return null;
            
            return (
              <div key={group.label} className="space-y-0.5">
                <button
                  onClick={() => toggleMenu(group.label)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg transition-colors text-xs ${
                    group.disabled
                      ? "text-slate-400"
                      : hasActiveItem 
                        ? "bg-primary/10 text-primary"
                        : "text-slate-600 hover:bg-white/50 hover:text-slate-900"
                  }`}
                >
                  <div className="flex items-center">
                    <span className="font-medium">{group.label}</span>
                    {group.badge && (
                      <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-bold">
                        {group.badge}
                      </span>
                    )}
                  </div>
                  {isExpanded ? (
                    <ChevronDown className="w-4 h-4" />
                  ) : (
                    <ChevronRight className="w-4 h-4" />
                  )}
                </button>
                
                {isExpanded && (
                  <div className="ml-4 pl-2 border-l border-slate-200 space-y-0.5">
                    {visibleItems.map((item) => {
                      const isActive = location.pathname === item.path || location.pathname + location.search === item.path;
                      
                      // Se o item estiver desabilitado, renderiza como div em vez de Link
                      if (item.disabled) {
                        return (
                          <div
                            key={item.path}
                            className="flex items-center px-3 py-2 rounded-lg text-xs text-slate-400 cursor-not-allowed"
                          >
                            <span className="font-medium">{item.label}</span>
                            {item.badge && (
                              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded font-bold bg-amber-100 text-amber-700">
                                {item.badge}
                              </span>
                            )}
                          </div>
                        );
                      }
                      
                      return (
                        <Link
                          key={item.path}
                          to={item.path}
                          data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                          className={`flex items-center px-3 py-2 rounded-lg transition-colors text-xs text-left ${
                            isActive
                              ? "bg-primary text-white shadow-md shadow-primary/20"
                              : "text-slate-600 hover:bg-white/50 hover:text-slate-900"
                          }`}
                        >
                          <span className="font-medium">{item.label}</span>
                          {item.badge && (
                            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded font-bold ${
                              isActive 
                                ? "bg-white/20 text-white" 
                                : "bg-primary/10 text-primary"
                            }`}>
                              {item.badge}
                            </span>
                          )}
                          {item.dynamicBadge === "bolsao" && bolsaoCount > 0 && (
                            <span className={`ml-auto text-[10px] px-1.5 py-0.5 rounded-full font-bold ${
                              isActive 
                                ? "bg-white/20 text-white" 
                                : "bg-amber-500 text-white"
                            }`}>
                              {bolsaoCount}
                            </span>
                          )}
                        </Link>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
          
          {/* Meu Perfil - Fora dos grupos */}
          <div className="pt-2 border-t border-slate-200">
            <Link
              to={`${prefix}/profile`}
              data-testid="nav-meu-perfil"
              className={`flex items-center px-3 py-2 rounded-lg transition-colors text-xs ${
                location.pathname === `${prefix}/profile`
                  ? "bg-primary text-white shadow-md shadow-primary/20"
                  : "text-slate-600 hover:bg-white/50 hover:text-slate-900"
              }`}
            >
              <span className="font-medium">Meu Perfil</span>
            </Link>
          </div>
        </nav>

        {/* Botão Sair - Sempre visível no rodapé */}
        <div className="pt-3 mt-auto border-t border-slate-200 flex-shrink-0">
          <button
            onClick={onLogout}
            data-testid="logout-button"
            className="w-full flex items-center justify-center gap-2.5 px-3 py-2.5 rounded-xl bg-red-50 text-red-600 hover:bg-red-100 border border-red-200 transition-colors text-sm font-semibold"
          >
            <LogOut className="w-4 h-4" strokeWidth={1.5} />
            <span>Sair</span>
          </button>
          <p className="text-center text-[10px] text-slate-400 mt-2 tracking-wide">InstaIMOB Construtoras V.1.0.0</p>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
